import { browser } from "$app/environment"
import {
  Experiment,
  type ExperimentClient,
} from "@amplitude/experiment-js-client"
import { isAmplitudeInitialized } from "@tastyworks/shared-amplitude-analytics/amplitude"
import { get, readable, type Readable } from "svelte/store"

const AMPLITUDE_FETCH_TIMEOUT_MILLIS = 5000

// If Amplitude is slow to start (e.g. remote config hangs), stop waiting and use flag defaults.
// The SDK waits up to 10s internally to resolve the analytics identity before it even starts fetching flags.
const EXPERIMENT_STARTUP_TIMEOUT_MILLIS = 5000

function withStartupTimeout<V>(promise: Promise<V>): Promise<V> {
  const startupTimeout = new Promise<never>((_, reject) =>
    setTimeout(
      () => reject(new Error("Amplitude Experiment startup timed out")),
      EXPERIMENT_STARTUP_TIMEOUT_MILLIS
    )
  )
  return Promise.race([promise, startupTimeout])
}

/**
 * Options for the FlagStore.
 *
 * @template T - The type of the flag value.
 * @property {T} defaultValue - The value used when Amplitude has no variant.
 */
type FlagStoreOptions<T> = {
  defaultValue: T
}

// Sent as Amplitude Experiment user properties for flag targeting
export type FlagTargeting = {
  isEmployeeDomain: boolean
  isNewUser: boolean
  isMobile: boolean | undefined
}

export class FeatureFlagClient {
  private experiment?: ExperimentClient

  constructor(private readonly targeting?: FlagTargeting) {}

  async initialize() {
    await this.initializeExperiment()

    if (browser) {
      console.debug("flags", this.unwrappedFlags)
    }
  }

  private async initializeExperiment() {
    const deploymentKey = import.meta.env.VITE_AMPLITUDE_DEPLOYMENT_KEY
    if (!deploymentKey || !isAmplitudeInitialized()) {
      return
    }
    const experiment = Experiment.initializeWithAmplitudeAnalytics(
      deploymentKey,
      {
        automaticExposureTracking: false,
        fetchTimeoutMillis: AMPLITUDE_FETCH_TIMEOUT_MILLIS,
        throwOnError: true,
      }
    )
    try {
      await withStartupTimeout(
        experiment
          .setTracksAssignment(false)
          .then(() =>
            experiment.fetch({ user_properties: { ...this.targeting } })
          )
      )
      this.experiment = experiment
    } catch (e) {
      console.error("Amplitude Experiment fetch failed", e)
    }
  }

  private amplitudeValue<V>(key: string, defaultValue: V): V | undefined {
    const variantKey = this.experiment?.variant(key)?.key
    if (variantKey === undefined) {
      return undefined
    }
    if (typeof defaultValue === "boolean") {
      if (variantKey === "on") return true as V
      if (variantKey === "off") return false as V
    }
    return undefined
  }

  get flags() {
    return {
      beneficialOwnersPhone: this.flagStore<boolean>(
        "ao2-beneficial-owners-phone",
        {
          defaultValue: false,
        }
      ),
      supportCustomerResume: this.flagStore<boolean>("ao2-resume-customer", {
        defaultValue: false,
      }),
      supportApplicationResume: this.flagStore<boolean>(
        "ao-resume-application",
        {
          defaultValue: false,
        }
      ),
      plaidLayerEnabled: this.flagStore<boolean>("ao-plaid-layer", {
        defaultValue: false,
      }),
    }
  }

  get unwrappedFlags() {
    return Object.fromEntries(
      Object.entries(this.flags).map(([key, flag]) => [key, get(flag)])
    )
  }

  /**
   * create a readable store for a feature flag.
   *
   * @template T - The type of the flag value.
   * @param {string} key - The key of the feature flag.
   * @param {FlagStoreOptions<T>} options - The options for retrieving the flag value.
   * @returns {Readable<T>} - The readable store containing the flag value.
   */
  private flagStore<T>(
    key: string,
    { defaultValue }: FlagStoreOptions<T>
  ): Readable<T> {
    return readable(this.amplitudeValue(key, defaultValue) ?? defaultValue)
  }
}

// Infrastructure for adding navigation flags for dynamic routing
export type NavFlags = {} // oxlint-disable-line
